/*
 * Minimal, dependency-free JWT payload decoder.
 *
 * We never verify the signature client-side (the server is authoritative) — we
 * only read non-sensitive claims to drive the UI: which brand the current
 * token is scoped to, the caller's role within it, and expiry. A principal-only
 * token (super-admin with no active brand) simply has no `brand_id`/`role`
 * claim.
 *
 * Tolerant by design: any malformed/absent claim yields `null` fields rather
 * than throwing, so a server claim-name change degrades to "unknown", not a
 * crash.
 */

import type { Role } from "../api/client";

export interface TokenClaims {
  /** Subject — the principal/user id, if present. */
  sub: string | null;
  /** Brand the token is scoped to; null for principal-only tokens. */
  brandId: string | null;
  /** Caller's role within `brandId`, if the claim is present + valid. */
  role: Role | null;
  /** Expiry as epoch seconds, if present. */
  exp: number | null;
  /** True when the token carries `is_super_admin:true` (global super-admin). */
  isSuperAdmin: boolean;
}

const ROLES: ReadonlySet<string> = new Set(["viewer", "operator", "admin"]);

function base64UrlDecode(segment: string): string | null {
  try {
    const padded = segment
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(segment.length / 4) * 4, "=");
    const binary = atob(padded);
    // Handle UTF-8 payloads correctly.
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

/** Decode a JWT's claims, or return all-null fields if it cannot be parsed. */
export function decodeToken(token: string | null): TokenClaims {
  const empty: TokenClaims = {
    sub: null,
    brandId: null,
    role: null,
    exp: null,
    isSuperAdmin: false,
  };
  if (!token) return empty;

  const parts = token.split(".");
  if (parts.length < 2) return empty;

  const json = base64UrlDecode(parts[1]);
  if (!json) return empty;

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return empty;
  }

  const roleRaw = pickString(payload, "role");
  const role = roleRaw && ROLES.has(roleRaw) ? (roleRaw as Role) : null;

  return {
    sub: pickString(payload, "sub", "user_id"),
    brandId: pickString(payload, "brand_id", "tid"),
    role,
    exp: typeof payload.exp === "number" ? payload.exp : null,
    isSuperAdmin: payload.is_super_admin === true,
  };
}

/** True if the token carries an `exp` claim that is already in the past. */
export function isTokenExpired(token: string | null): boolean {
  const { exp } = decodeToken(token);
  if (exp === null) return false; // no expiry claim → treat as non-expiring here
  return exp * 1000 <= Date.now();
}
